/**
 * Concierge — REST client.
 */

import api from '@services/api';
import type { ConciergeAgentCard, ConciergeConversation, ConciergeMessage } from '../types';

/** A shipped persona edition — the platform default, or a packaged one. */
export interface ConciergePersonaPreset {
  key: string;
  label: string;
  description: string;
  system_prompt: string;
}

export interface ConciergeConfig {
  /** This tenant's own text. Stays stored even while a shipped persona runs. */
  system_prompt: string;
  persona_source: 'local' | 'inherit';
  publish_to_descendants: boolean;
  /** True when a SHIPPED persona runs instead of `system_prompt`. */
  use_default_persona: boolean;
  /** Which shipped edition `use_default_persona` selects. */
  persona_preset: string;
  presets: ConciergePersonaPreset[];
  /** What runs today, and whose decision that is. */
  persona_origin: 'local' | 'inherited' | 'platform_default';
  persona_origin_tenant_name: string | null;
  /** What "inherit" would give this tenant — present even while overriding. */
  inheritable_from: { tenant_id: string; tenant_name: string; system_prompt: string | null } | null;
  descendant_reach: { following: number; overriding: number };
  can_edit: boolean;
  /** The Concierge's model slot — the SAME tenant_settings key the super-admin
   *  Meta-agents tab writes. `default_model` is a family key or a release id
   *  (a platform admin may pin one); `allowed_models` [] = every family. */
  model?: {
    default_model: string;
    allowed_models: string[];
    is_default: boolean;
    platform_default: string;
  };
}

export const conciergeApi = {
  /** The tenant's Concierge persona config (group_admin+ only). */
  async getConfig(): Promise<ConciergeConfig> {
    const { data } = await api.get('/concierge/config');
    return data as ConciergeConfig;
  },

  /** Save the persona. Applies to every user in the tenant. */
  async saveConfig(patch: {
    system_prompt?: string | null;
    persona_source?: 'local' | 'inherit';
    publish_to_descendants?: boolean;
    use_default_persona?: boolean;
    persona_preset?: string;
  }): Promise<ConciergeConfig> {
    const { data } = await api.put('/concierge/config', patch);
    return data as ConciergeConfig;
  },

  /** The model the Concierge starts a conversation on, for this tenant. A
   *  family key; null resets to the platform default. Same gate as the persona
   *  editor; writes the key the super-admin Meta-agents tab edits. */
  async updateDefaultModel(defaultModel: string | null): Promise<NonNullable<ConciergeConfig['model']>> {
    const { data } = await api.patch('/concierge/config/default-model', { default_model: defaultModel });
    return (data as { model: NonNullable<ConciergeConfig['model']> }).model;
  },

  async createConversation(title?: string): Promise<ConciergeConversation> {
    const { data } = await api.post('/concierge/conversations', { title });
    return data as ConciergeConversation;
  },

  /** List the current user's Concierge sessions, most-recent first. */
  async listConversations(
    opts: { limit?: number; offset?: number } = {},
  ): Promise<{
    conversations: ConciergeConversation[];
    total: number;
    has_more: boolean;
    /** Carried here so the page can fill the model picker WITHOUT creating a
     *  conversation just to read it. */
    model_config?: { default_model: string | null; allowed_models: string[] };
  }> {
    const limit = opts.limit ?? 50;
    const offset = opts.offset ?? 0;
    const { data } = await api.get('/concierge/conversations', { params: { limit, offset } });
    return {
      conversations: (data?.conversations ?? []) as ConciergeConversation[],
      total: data?.total ?? 0,
      has_more: Boolean(data?.has_more),
      model_config: data?.model_config,
    };
  },

  async getConversation(
    id: string,
  ): Promise<ConciergeConversation & {
    messages: ConciergeMessage[];
    /** Cards surfaced during this conversation, so history can replay them. */
    agent_cards?: (ConciergeAgentCard & { shown_at?: string })[];
  }> {
    const { data } = await api.get(`/concierge/conversations/${id}`);
    return data;
  },

  async deleteConversation(id: string): Promise<void> {
    await api.delete(`/concierge/conversations/${id}`);
  },
};
